import React, { useEffect } from "react";
import Header from "../../../components/Header";
import Footer from "../../../components/Footer";
import "./Healthcare.css";

import {
  FiArrowRight,
  FiActivity,
  FiMessageSquare,
  FiCalendar,
  FiFileText,
  FiSearch,
  FiUsers,
  FiZap,
  FiCheckCircle,
  FiBarChart2,
  FiRefreshCw,
  FiDatabase,
  FiClock,
  FiLayers,
  FiTarget,
  FiClipboard,
  FiHeart,
  FiPhoneCall,
} from "react-icons/fi";

const Healthcare = () => {
  useEffect(() => {
    document.title = "AI for Healthcare | Dropgear IO";
  }, []);

  return (
    <>
      <Header />

      <main className="healthcare-page">

        {/* =====================================================
            HERO
        ===================================================== */}

        <section className="healthcare-hero">

          <div className="healthcare-hero-grid">

            {/* LEFT */}

            <div className="healthcare-hero-content">

              <span className="healthcare-eyebrow">
                HEALTHCARE
              </span>

              <h1>
                AI for
                <span> healthcare workflows.</span>
              </h1>

              <p>
                Dropgear IO helps healthcare organizations bring AI
                into everyday operational workflows — from patient
                communication and scheduling to documentation,
                information access, and administrative processes.
              </p>

              <div className="healthcare-hero-actions">

                <a
                  href="/contact"
                  className="healthcare-primary-btn"
                >
                  Connect to Our Team
                  <FiArrowRight />
                </a>

                <a
                  href="#healthcare-solutions"
                  className="healthcare-secondary-btn"
                >
                  Explore Solutions
                </a>

              </div>

              <div className="healthcare-hero-note">

                <FiCheckCircle />

                <span>
                  Designed for practical healthcare operations
                </span>

              </div>

            </div>


            {/* RIGHT */}

            <div className="healthcare-hero-visual">

              <div className="healthcare-visual-glow"></div>

              <div className="healthcare-dashboard">

                <div className="healthcare-dashboard-header">

                  <div>

                    <span>
                      HEALTHCARE OPERATIONS
                    </span>

                    <strong>
                      AI Workflow Workspace
                    </strong>

                  </div>

                  <div className="healthcare-live-status">
                    <i></i>
                    Live
                  </div>

                </div>


                {/* METRICS */}

                <div className="healthcare-metrics">

                  <div className="healthcare-metric-card">

                    <span>
                      REQUESTS
                    </span>

                    <strong>
                      842
                    </strong>

                    <small>
                      Active
                    </small>

                  </div>


                  <div className="healthcare-metric-card">

                    <span>
                      APPOINTMENTS
                    </span>

                    <strong>
                      126
                    </strong>

                    <small>
                      Today
                    </small>

                  </div>


                  <div className="healthcare-metric-card">

                    <span>
                      AI TASKS
                    </span>

                    <strong>
                      318
                    </strong>

                    <small>
                      Assisted
                    </small>

                  </div>

                </div>


                {/* WORKFLOW */}

                <div className="healthcare-workflow">

                  <div className="healthcare-workflow-title">

                    <span>
                      AI WORKFLOW
                    </span>

                    <FiActivity />

                  </div>


                  <div className="healthcare-workflow-row">

                    <div className="healthcare-workflow-icon">
                      <FiMessageSquare />
                    </div>

                    <div>

                      <strong>
                        Patient request
                      </strong>

                      <span>
                        Appointment information
                      </span>

                    </div>

                    <FiCheckCircle />

                  </div>


                  <div className="healthcare-workflow-line"></div>


                  <div className="healthcare-workflow-row">

                    <div className="healthcare-workflow-icon">
                      <FiSearch />
                    </div>

                    <div>

                      <strong>
                        Information retrieved
                      </strong>

                      <span>
                        Relevant operational context
                      </span>

                    </div>

                    <FiCheckCircle />

                  </div>


                  <div className="healthcare-workflow-line"></div>


                  <div className="healthcare-workflow-row active">

                    <div className="healthcare-workflow-icon">
                      <FiZap />
                    </div>

                    <div>

                      <strong>
                        Response prepared
                      </strong>

                      <span>
                        Ready for review
                      </span>

                    </div>

                    <span className="healthcare-working">
                      Working
                    </span>

                  </div>

                </div>

              </div>


              <div className="healthcare-floating-card healthcare-float-one">

                <FiCalendar />

                <span>
                  Appointment workflow updated
                </span>

              </div>


              <div className="healthcare-floating-card healthcare-float-two">

                <FiFileText />

                <span>
                  Document assistance completed
                </span>

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            INTRO
        ===================================================== */}

        <section className="healthcare-intro">

          <div className="healthcare-container">

            <div className="healthcare-intro-grid">

              <div>

                <span className="healthcare-section-label">
                  THE HEALTHCARE WORKFLOW
                </span>

                <h2>
                  Healthcare creates
                  <strong> complex daily work.</strong>
                </h2>

              </div>


              <div>

                <p>
                  Healthcare teams manage much more than direct
                  patient interactions. Scheduling, communication,
                  documentation, coordination, information access,
                  and administrative processes all require time
                  and attention.
                </p>

                <p>
                  Dropgear IO brings AI into these workflows to
                  help teams process information, support routine
                  tasks, and move operational work forward.
                </p>

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            SOLUTIONS
        ===================================================== */}

        <section
          id="healthcare-solutions"
          className="healthcare-solutions"
        >

          <div className="healthcare-container">

            <div className="healthcare-section-heading">

              <span>
                AI FOR HEALTHCARE
              </span>

              <h2>
                Build smarter
                <br />
                healthcare <strong>workflows.</strong>
              </h2>

              <p>
                Apply AI to practical operational workflows while
                keeping people at the center of important decisions.
              </p>

            </div>


            <div className="healthcare-solutions-grid">

              {/* 01 */}

              <div className="healthcare-solution-card featured">

                <div className="healthcare-solution-icon">
                  <FiMessageSquare />
                </div>

                <span>
                  01
                </span>

                <h3>
                  Patient Communication
                </h3>

                <p>
                  Support routine patient-facing communication
                  and information requests through AI-assisted
                  experiences.
                </p>

                <ul>

                  <li>
                    <FiCheckCircle />
                    Common information requests
                  </li>

                  <li>
                    <FiCheckCircle />
                    Appointment information
                  </li>

                  <li>
                    <FiCheckCircle />
                    Communication assistance
                  </li>

                </ul>

              </div>


              {/* 02 */}

              <div className="healthcare-solution-card">

                <div className="healthcare-solution-icon">
                  <FiCalendar />
                </div>

                <span>
                  02
                </span>

                <h3>
                  Scheduling Assistance
                </h3>

                <p>
                  Help teams manage scheduling-related requests
                  and make appointment workflows easier to handle.
                </p>

                <ul>

                  <li>
                    <FiCheckCircle />
                    Scheduling requests
                  </li>

                  <li>
                    <FiCheckCircle />
                    Appointment information
                  </li>

                  <li>
                    <FiCheckCircle />
                    Workflow assistance
                  </li>

                </ul>

              </div>


              {/* 03 */}

              <div className="healthcare-solution-card">

                <div className="healthcare-solution-icon">
                  <FiFileText />
                </div>

                <span>
                  03
                </span>

                <h3>
                  Documentation Assistance
                </h3>

                <p>
                  Support teams with structured information,
                  document preparation, and repetitive
                  documentation workflows.
                </p>

                <ul>

                  <li>
                    <FiCheckCircle />
                    Information organization
                  </li>

                  <li>
                    <FiCheckCircle />
                    Document preparation
                  </li>

                  <li>
                    <FiCheckCircle />
                    Workflow support
                  </li>

                </ul>

              </div>


              {/* 04 */}

              <div className="healthcare-solution-card">

                <div className="healthcare-solution-icon">
                  <FiSearch />
                </div>

                <span>
                  04
                </span>

                <h3>
                  Information Access
                </h3>

                <p>
                  Help authorized teams find relevant operational
                  information more quickly across connected sources.
                </p>

                <ul>

                  <li>
                    <FiCheckCircle />
                    Information retrieval
                  </li>

                  <li>
                    <FiCheckCircle />
                    Knowledge assistance
                  </li>

                  <li>
                    <FiCheckCircle />
                    Context discovery
                  </li>

                </ul>

              </div>


              {/* 05 */}

              <div className="healthcare-solution-card">

                <div className="healthcare-solution-icon">
                  <FiBarChart2 />
                </div>

                <span>
                  05
                </span>

                <h3>
                  Operational Intelligence
                </h3>

                <p>
                  Help healthcare teams understand operational
                  information and investigate recurring workflow
                  questions.
                </p>

                <ul>

                  <li>
                    <FiCheckCircle />
                    Operational insights
                  </li>

                  <li>
                    <FiCheckCircle />
                    Trend exploration
                  </li>

                  <li>
                    <FiCheckCircle />
                    Information analysis
                  </li>

                </ul>

              </div>


              {/* 06 */}

              <div className="healthcare-solution-card">

                <div className="healthcare-solution-icon">
                  <FiRefreshCw />
                </div>

                <span>
                  06
                </span>

                <h3>
                  Workflow Automation
                </h3>

                <p>
                  Support repetitive administrative workflows by
                  connecting AI with defined business processes.
                </p>

                <ul>

                  <li>
                    <FiCheckCircle />
                    Repetitive tasks
                  </li>

                  <li>
                    <FiCheckCircle />
                    Task routing
                  </li>

                  <li>
                    <FiCheckCircle />
                    Process assistance
                  </li>

                </ul>

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            PATIENT EXPERIENCE
        ===================================================== */}

        <section className="healthcare-patient">

          <div className="healthcare-container">

            <div className="healthcare-two-column">

              {/* VISUAL */}

              <div className="healthcare-feature-visual">

                <div className="healthcare-chat-window">

                  <div className="healthcare-chat-header">

                    <div className="healthcare-agent-avatar">
                      <FiHeart />
                    </div>

                    <div>

                      <strong>
                        Dropgear AI
                      </strong>

                      <span>
                        Healthcare Assistant
                      </span>

                    </div>

                    <span className="healthcare-chat-online">
                      ●
                    </span>

                  </div>


                  <div className="healthcare-chat-body">

                    <div className="healthcare-message patient">
                      I need to reschedule my appointment.
                    </div>

                    <div className="healthcare-message ai">
                      I can help with the scheduling request.
                      Let me check the available appointment
                      options for you.
                    </div>

                    <div className="healthcare-appointment-card">

                      <div className="healthcare-calendar-icon">
                        <FiCalendar />
                      </div>

                      <div>

                        <strong>
                          Available appointment
                        </strong>

                        <span>
                          Tuesday · 10:30 AM
                        </span>

                      </div>

                      <FiArrowRight />

                    </div>

                  </div>

                </div>

              </div>


              {/* CONTENT */}

              <div className="healthcare-feature-content">

                <span className="healthcare-section-label">
                  PATIENT EXPERIENCE
                </span>

                <h2>
                  Make routine
                  <strong> interactions simpler.</strong>
                </h2>

                <p>
                  Patients and healthcare teams interact with
                  organizations through many routine requests.
                  AI can help organize and support these
                  interactions while keeping appropriate human
                  oversight in the workflow.
                </p>


                <div className="healthcare-check-list">

                  <div>

                    <FiCheckCircle />

                    <span>
                      Appointment-related assistance
                    </span>

                  </div>

                  <div>

                    <FiCheckCircle />

                    <span>
                      Routine information requests
                    </span>

                  </div>

                  <div>

                    <FiCheckCircle />

                    <span>
                      Communication workflow support
                    </span>

                  </div>

                  <div>

                    <FiCheckCircle />

                    <span>
                      Administrative assistance
                    </span>

                  </div>

                </div>

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            ADMINISTRATIVE OPERATIONS
        ===================================================== */}

        <section className="healthcare-operations">

          <div className="healthcare-container">

            <div className="healthcare-section-heading centered">

              <span>
                ADMINISTRATIVE OPERATIONS
              </span>

              <h2>
                Reduce repetitive work.
                <br />
                <strong>Support your teams.</strong>
              </h2>

              <p>
                AI can assist teams with repetitive information
                and administrative workflows so people can spend
                more time on work requiring judgment and care.
              </p>

            </div>


            <div className="healthcare-operations-grid">

              <div className="healthcare-operation-card">

                <div className="healthcare-operation-icon">
                  <FiClipboard />
                </div>

                <h3>
                  Administrative Tasks
                </h3>

                <p>
                  Support repetitive operational tasks that
                  require information processing and coordination.
                </p>

              </div>


              <div className="healthcare-operation-card">

                <div className="healthcare-operation-icon">
                  <FiDatabase />
                </div>

                <h3>
                  Information Management
                </h3>

                <p>
                  Help teams organize and access relevant
                  information within defined workflows.
                </p>

              </div>


              <div className="healthcare-operation-card">

                <div className="healthcare-operation-icon">
                  <FiLayers />
                </div>

                <h3>
                  Workflow Coordination
                </h3>

                <p>
                  Connect multiple operational steps into
                  clearer AI-assisted workflows.
                </p>

              </div>


              <div className="healthcare-operation-card">

                <div className="healthcare-operation-icon">
                  <FiTarget />
                </div>

                <h3>
                  Decision Support
                </h3>

                <p>
                  Help teams investigate operational information
                  while keeping important decisions with people.
                </p>

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            USE CASES
        ===================================================== */}

        <section className="healthcare-usecases">

          <div className="healthcare-container">

            <div className="healthcare-section-heading">

              <span>
                PRACTICAL USE CASES
              </span>

              <h2>
                Start with a workflow.
                <br />
                <strong>Build from there.</strong>
              </h2>

              <p>
                The most useful AI systems begin with a clearly
                defined problem. Choose a workflow where AI can
                provide practical assistance and expand over time.
              </p>

            </div>


            <div className="healthcare-usecase-list">

              <div className="healthcare-usecase">

                <span>
                  01
                </span>

                <FiPhoneCall />

                <div>

                  <h3>
                    Patient Communication Assistant
                  </h3>

                  <p>
                    Support routine questions and information
                    requests through an AI-assisted communication
                    workflow.
                  </p>

                </div>

                <FiArrowRight />

              </div>


              <div className="healthcare-usecase">

                <span>
                  02
                </span>

                <FiCalendar />

                <div>

                  <h3>
                    Appointment Workflow Assistant
                  </h3>

                  <p>
                    Assist teams and users with appointment-related
                    information and scheduling workflows.
                  </p>

                </div>

                <FiArrowRight />

              </div>


              <div className="healthcare-usecase">

                <span>
                  03
                </span>

                <FiFileText />

                <div>

                  <h3>
                    Documentation Assistant
                  </h3>

                  <p>
                    Help organize information and support repetitive
                    document preparation workflows.
                  </p>

                </div>

                <FiArrowRight />

              </div>


              <div className="healthcare-usecase">

                <span>
                  04
                </span>

                <FiBarChart2 />

                <div>

                  <h3>
                    Operations Intelligence Assistant
                  </h3>

                  <p>
                    Help teams explore operational information
                    and identify workflow patterns.
                  </p>

                </div>

                <FiArrowRight />

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            HUMAN + AI
        ===================================================== */}

        <section className="healthcare-human">

          <div className="healthcare-container">

            <div className="healthcare-human-grid">

              <div>

                <span className="healthcare-section-label">
                  HUMAN + AI
                </span>

                <h2>
                  AI should support
                  <strong> people, not replace judgment.</strong>
                </h2>

                <p>
                  Healthcare workflows often involve decisions
                  where context, expertise, and human responsibility
                  matter. Dropgear IO focuses on using AI as an
                  assistant within defined workflows, with people
                  remaining in control of important decisions.
                </p>

              </div>


              <div className="healthcare-principles">

                <div>

                  <FiUsers />

                  <section>

                    <h3>
                      Human oversight
                    </h3>

                    <p>
                      Keep appropriate human review within
                      workflows where judgment is required.
                    </p>

                  </section>

                </div>


                <div>

                  <FiLayers />

                  <section>

                    <h3>
                      Defined workflows
                    </h3>

                    <p>
                      Build AI around specific operational
                      processes instead of uncontrolled automation.
                    </p>

                  </section>

                </div>


                <div>

                  <FiTarget />

                  <section>

                    <h3>
                      Practical outcomes
                    </h3>

                    <p>
                      Focus on making real workflows easier,
                      clearer, and more efficient.
                    </p>

                  </section>

                </div>

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            WHY DROPGEAR
        ===================================================== */}

        <section className="healthcare-why">

          <div className="healthcare-container">

            <div className="healthcare-why-grid">

              <div>

                <span className="healthcare-section-label">
                  WHY DROPGEAR IO
                </span>

                <h2>
                  AI designed around
                  <strong> the work itself.</strong>
                </h2>

                <p>
                  Healthcare organizations need more than a
                  chatbot. They need AI systems that can fit into
                  the workflows their teams already use.
                </p>

                <a
                  href="/contact"
                  className="healthcare-primary-btn"
                >
                  Discuss Your Workflow
                  <FiArrowRight />
                </a>

              </div>


              <div className="healthcare-why-list">

                <div>

                  <FiTarget />

                  <section>

                    <h3>
                      Workflow-focused
                    </h3>

                    <p>
                      Begin with a clearly defined operational
                      problem and build around it.
                    </p>

                  </section>

                </div>


                <div>

                  <FiLayers />

                  <section>

                    <h3>
                      Flexible AI systems
                    </h3>

                    <p>
                      Combine AI models, automation, agents,
                      information, and business rules where useful.
                    </p>

                  </section>

                </div>


                <div>

                  <FiZap />

                  <section>

                    <h3>
                      Practical implementation
                    </h3>

                    <p>
                      Focus on useful workflows rather than
                      adding AI simply for the sake of technology.
                    </p>

                  </section>

                </div>

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            CTA
        ===================================================== */}

        <section className="healthcare-final-cta">

          <div className="healthcare-final-glow"></div>

          <div className="healthcare-final-content">

            <span>
              HEALTHCARE
            </span>

            <h2>
              Ready to explore
              <br />
              AI for healthcare workflows?
            </h2>

            <p>
              Tell Dropgear IO about a workflow, operational
              challenge, or repetitive process you want to
              improve with AI.
            </p>

            <a
              href="/contact"
              className="healthcare-cta-button"
            >
              Start a Conversation
              <FiArrowRight />
            </a>

          </div>

        </section>

      </main>

      <Footer />
    </>
  );
};

export default Healthcare;